import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Send, Bell, Vibrate, AlertTriangle, MessageSquare, Trash2, Clock } from 'lucide-react';
import { getTheme, buildCustomTheme } from '../config/colorThemes';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Broadcast() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [theme, setTheme] = useState(null);
  
  // Form state
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('normal');
  const [playSound, setPlaySound] = useState(false);
  const [vibrate, setVibrate] = useState(false);

  useEffect(() => {
    fetchEventBySlug();
  }, [slug]);

  useEffect(() => {
    if (event) {
      fetchBroadcasts();
      // Load theme
      if (event.theme_mode === 'customization' && event.custom_colors) {
        setTheme(buildCustomTheme(event.custom_colors));
      } else {
        setTheme(getTheme(event.color_theme || 'coastalSilk'));
      }
    }
  }, [event]);

  const fetchEventBySlug = async () => {
    try {
      // Fetch event by slug with authentication
      const response = await axios.get(`${API}/events/by-slug/${slug}`);
      setEvent(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to access the broadcast panel');
        navigate('/login');
      } else {
        toast.error('Event not found or you do not have access');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBroadcasts = async () => {
    if (!event) return;
    try {
      const response = await axios.get(`${API}/events/${event.id}/broadcasts`);
      setBroadcasts(response.data);
    } catch (error) {
      console.error('Failed to fetch broadcasts');
    }
  };

  const handleSendBroadcast = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/events/${event.id}/broadcasts`, {
        message: message.trim(),
        message_type: messageType,
        play_sound: playSound,
        vibrate: vibrate
      });
      
      toast.success('Message sent to all guests!');
      setMessage('');
      setMessageType('normal');
      setPlaySound(false);
      setVibrate(false);
      fetchBroadcasts();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteBroadcast = async (broadcastId) => {
    try {
      await axios.delete(`${API}/events/${event.id}/broadcasts/${broadcastId}`);
      toast.success('Message deleted');
      fetchBroadcasts();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const colors = theme?.colors || getTheme('midnightGold').colors;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-pulse font-heading text-xl" style={{ color: colors.primary }}>Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <h1 className="font-heading text-3xl mb-4" style={{ color: colors.primary }}>Event Not Found</h1>
          <p style={{ color: colors.textSecondary }}>You don't have access to this broadcast panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ backgroundColor: colors.background }}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/events/${event.id}`)}
            style={{ color: colors.textSecondary }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Event
          </Button>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2" style={{ color: colors.primary }}>
            <MessageSquare className="inline h-7 w-7 mr-2" />
            Broadcast Message
          </h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            {event.name}
          </p>
        </div>

        {/* Broadcast Form */}
        <div 
          className="rounded-xl p-6 mb-8"
          style={{ 
            backgroundColor: colors.surface || `${colors.background}ee`,
            border: `1px solid ${colors.secondary}40`
          }}
        >
          <div className="space-y-4">
            {/* Message Input */}
            <div>
              <Label className="text-sm font-medium mb-2 block" style={{ color: colors.text }}>
                Message
              </Label>
              <Textarea
                placeholder="Type your announcement..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[100px] resize-none"
                style={{ 
                  backgroundColor: colors.background,
                  borderColor: `${colors.secondary}40`,
                  color: colors.text
                }}
                data-testid="broadcast-message-input"
              />
            </div>

            {/* Message Type */}
            <div className="flex gap-4">
              <button
                onClick={() => setMessageType('normal')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  messageType === 'normal' ? 'ring-2' : ''
                }`}
                style={{ 
                  backgroundColor: messageType === 'normal' ? `${colors.secondary}20` : colors.background,
                  borderColor: messageType === 'normal' ? colors.secondary : `${colors.secondary}40`,
                  color: colors.text
                }}
                data-testid="broadcast-type-normal"
              >
                <Bell className="h-5 w-5 mx-auto mb-1" style={{ color: colors.secondary }} />
                <div className="text-sm font-medium">Normal</div>
                <div className="text-xs" style={{ color: colors.textSecondary }}>Auto-dismiss (10s)</div>
              </button>
              
              <button
                onClick={() => setMessageType('urgent')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  messageType === 'urgent' ? 'ring-2' : ''
                }`}
                style={{ 
                  backgroundColor: messageType === 'urgent' ? '#fee2e220' : colors.background,
                  borderColor: messageType === 'urgent' ? '#ef4444' : `${colors.secondary}40`,
                  color: colors.text
                }}
                data-testid="broadcast-type-urgent"
              >
                <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-500" />
                <div className="text-sm font-medium">Urgent</div>
                <div className="text-xs" style={{ color: colors.textSecondary }}>Tap to close</div>
              </button>
            </div>

            {/* Sound & Vibration Options */}
            <div className="flex gap-6 pt-2">
              <div className="flex items-center gap-2">
                <Switch
                  id="play-sound"
                  checked={playSound}
                  onCheckedChange={setPlaySound}
                  data-testid="broadcast-sound-toggle"
                />
                <Label htmlFor="play-sound" className="text-sm flex items-center gap-1" style={{ color: colors.text }}>
                  <Bell className="h-4 w-4" /> Sound
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="vibrate"
                  checked={vibrate}
                  onCheckedChange={setVibrate}
                  data-testid="broadcast-vibrate-toggle"
                />
                <Label htmlFor="vibrate" className="text-sm flex items-center gap-1" style={{ color: colors.text }}>
                  <Vibrate className="h-4 w-4" /> Vibrate
                </Label>
              </div>
            </div>

            {/* Send Button */}
            <Button
              onClick={handleSendBroadcast}
              disabled={sending || !message.trim()}
              className="w-full py-6 text-lg font-medium"
              style={{ 
                backgroundColor: colors.secondary,
                color: colors.background
              }}
              data-testid="broadcast-send-btn"
            >
              {sending ? (
                'Sending...'
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Send to All Guests
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Previous Messages */}
        {broadcasts.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
              <Clock className="h-5 w-5" />
              Previous Messages
            </h2>
            <div className="space-y-3">
              {broadcasts.map((broadcast) => (
                <div
                  key={broadcast.id}
                  className="rounded-lg p-4 flex justify-between items-start gap-3"
                  style={{ 
                    backgroundColor: `${colors.secondary}10`,
                    border: `1px solid ${colors.secondary}30`
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {broadcast.message_type === 'urgent' ? (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Bell className="h-4 w-4" style={{ color: colors.secondary }} />
                      )}
                      <span className="text-xs" style={{ color: colors.textSecondary }}>
                        {format(new Date(broadcast.created_at), 'MMM d, HH:mm')}
                      </span>
                      {broadcast.play_sound && <Bell className="h-3 w-3" style={{ color: colors.textSecondary }} />}
                      {broadcast.vibrate && <Vibrate className="h-3 w-3" style={{ color: colors.textSecondary }} />}
                    </div>
                    <p className="text-sm" style={{ color: colors.text }}>{broadcast.message}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteBroadcast(broadcast.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Text */}
        <div className="mt-8 text-center text-sm" style={{ color: colors.textSecondary }}>
          <p>Messages will appear on all guests' screens who have the event page open.</p>
          <p className="mt-1">Bookmark this page for quick access during your event!</p>
        </div>
      </div>
    </div>
  );
}
